/**
 * Case dashboard — 個案看自己歷次 session 紀錄。
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Mast } from "../components/Mast.js";
import { api } from "../lib/api.js";
import { useAuth } from "../lib/auth.js";

interface SessionRow {
  id: string;
  scheduled_for: string;
  estimated_min: number;
  state: string;
  started_at?: string;
  ended_at?: string;
  case_name: string;
  trainer_name?: string;
}

export default function CaseDashboard() {
  const { user } = useAuth();
  const [sessions, setSessions] = useState<SessionRow[]>([]);
  const [loading, setLoading]   = useState(true);

  useEffect(() => {
    api<{ ok: true; sessions: SessionRow[] }>("/api/me/sessions")
      .then(r => setSessions(r.sessions))
      .finally(() => setLoading(false));
  }, []);

  const past     = sessions.filter(s => s.state === "ended"   || s.state === "aborted");
  const upcoming = sessions.filter(s => s.state === "planned" || s.state === "lobby");
  const live     = sessions.filter(s => s.state === "live"    || s.state === "paused");

  return (
    <>
      <Mast section="我的訓練" />
      <main className="page">
        <section style={{ marginBottom: "var(--s-8)" }}>
          <div className="serial rise rise-1" style={{ marginBottom: "var(--s-3)" }}>
            HELLO · {(user?.display_name ?? "").toUpperCase()}
          </div>
          <h1 className="rise rise-2" style={{ maxWidth: 760 }}>
            <span className="italic-display" style={{ color: "var(--rust)" }}>歡迎回來</span>，這裡是你的訓練紀錄
          </h1>
          <p className="rise rise-3 muted" style={{ marginTop: "var(--s-4)", fontSize: "var(--t-md)", maxWidth: 580, lineHeight: 1.6 }}>
            訓練師排定的訓練 session 會出現在這裡。session 結束後可看訓練師寫的回饋與訓練資料。
          </p>
        </section>

        {/* Live session — most prominent */}
        {live.length > 0 && (
          <section className="rise rise-4" style={{ marginBottom: "var(--s-7)" }}>
            <div className="section-head">
              <div>
                <div className="eyebrow" style={{ color: "var(--olive)" }}>● Live · 進行中</div>
                <h2>正在訓練</h2>
              </div>
            </div>
            {live.map(s => (
              <div key={s.id} className="surface rise" style={{
                padding: "var(--s-6)",
                background: "var(--ink)", color: "var(--paper)",
                borderColor: "var(--ink)",
                marginBottom: "var(--s-3)",
              }}>
                <div className="row row--between" style={{ marginBottom: "var(--s-3)" }}>
                  <span className="serial" style={{ color: "var(--rust)" }}>SID {s.id.slice(0, 12)}</span>
                  <span className="chip chip--olive">live</span>
                </div>
                <div className="italic-display" style={{ fontSize: "var(--t-2xl)", marginBottom: "var(--s-3)" }}>
                  {s.trainer_name ?? "訓練師"} · {fmtDate(s.scheduled_for)}
                </div>
                <Link to={`/me/sessions/${s.id}`} className="btn btn--rust">回到訓練 →</Link>
              </div>
            ))}
          </section>
        )}

        {/* Upcoming */}
        <section className="rise rise-5" style={{ marginBottom: "var(--s-8)" }}>
          <div className="section-head">
            <div>
              <div className="eyebrow">即將到來</div>
              <h2>排定的 sessions</h2>
            </div>
            <span className="muted" style={{ fontSize: "var(--t-sm)" }}>{upcoming.length} 筆</span>
          </div>

          {loading ? (
            <div className="muted">載入中…</div>
          ) : upcoming.length === 0 ? (
            <div className="empty">
              <div className="muted">目前沒有排定的訓練</div>
            </div>
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: "var(--s-4)" }}>
              {upcoming.map(s => (
                <article key={s.id} className="surface" style={{ padding: "var(--s-5)" }}>
                  <div className="row row--between" style={{ marginBottom: "var(--s-3)" }}>
                    <span className="serial">{s.id.slice(0, 8)}</span>
                    <span className="chip chip--rust">scheduled</span>
                  </div>
                  <div className="italic-display" style={{ fontSize: "var(--t-xl)", marginBottom: "var(--s-2)" }}>
                    {fmtDate(s.scheduled_for)}
                  </div>
                  <div className="kv" style={{ fontSize: "var(--t-sm)" }}>
                    <span className="kv__key">訓練師</span>
                    <span className="kv__val">{s.trainer_name ?? "—"}</span>
                    <span className="kv__key">時長</span>
                    <span className="kv__val tabular">{s.estimated_min} 分</span>
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>

        {/* Past sessions */}
        <section className="rise rise-6">
          <div className="section-head">
            <div>
              <div className="eyebrow">已完成 · {past.length} 次</div>
              <h2>歷次訓練</h2>
            </div>
          </div>

          {past.length === 0 ? (
            <div className="empty">
              <div className="muted">還沒有完成的訓練紀錄</div>
            </div>
          ) : (
            <div className="list">
              <div className="list-row" style={{
                borderBottom: "1px solid var(--ink)",
                paddingBottom: "var(--s-2)",
                marginBottom: "var(--s-1)",
              }}>
                <div className="serial">日期</div>
                <div className="serial">訓練師</div>
                <div className="serial">時長</div>
                <div className="serial" style={{ textAlign: "right" }}>動作</div>
              </div>
              {past.map(s => (
                <div key={s.id} className="list-row">
                  <div>
                    <div className="list-row__name">{fmtDate(s.scheduled_for)}</div>
                    <div className="list-row__meta">{s.id.slice(0, 12)}</div>
                  </div>
                  <div>{s.trainer_name ?? "—"}</div>
                  <div className="muted tabular">{s.estimated_min} 分</div>
                  <div style={{ textAlign: "right" }}>
                    <Link to={`/me/sessions/${s.id}`} style={{ fontSize: "var(--t-sm)" }}>看紀錄 →</Link>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </main>
    </>
  );
}

function fmtDate(s: string): string {
  if (!s) return "—";
  const d = new Date(s);
  if (isNaN(d.getTime())) return s;
  return d.toLocaleString("zh-TW", { month: "short", day: "numeric", weekday: "short", hour: "2-digit", minute: "2-digit" });
}
